import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utils/axiosConfig';

// Backend statuses: Draft, Pending, Assigned, InTransit, PaymentPending, Completed, Cancelled
const STATUS_CONFIG = {
  Draft:          { label: 'Draft',           color: '#6b7280', bg: '#f3f4f6', icon: '📝' },
  Pending:        { label: 'Pending',          color: '#d97706', bg: '#fef3c7', icon: '⏳' },
  Assigned:       { label: 'Rider Assigned',   color: '#2563eb', bg: '#dbeafe', icon: '🏍️' },
  InTransit:      { label: 'In Transit',       color: '#7c3aed', bg: '#ede9fe', icon: '🚀' },
  PaymentPending: { label: 'Payment Pending',  color: '#b45309', bg: '#fef3c7', icon: '💳' },
  Completed:      { label: 'Completed',        color: '#059669', bg: '#d1fae5', icon: '✅' },
  Cancelled:      { label: 'Cancelled',        color: '#dc2626', bg: '#fee2e2', icon: '❌' },
  // lowercase variants from older data
  pending:        { label: 'Pending',          color: '#d97706', bg: '#fef3c7', icon: '⏳' },
  assigned:       { label: 'Rider Assigned',   color: '#2563eb', bg: '#dbeafe', icon: '🏍️' },
  in_transit:     { label: 'In Transit',       color: '#7c3aed', bg: '#ede9fe', icon: '🚀' },
  payment_pending:{ label: 'Payment Pending',  color: '#b45309', bg: '#fef3c7', icon: '💳' },
  completed:      { label: 'Completed',        color: '#059669', bg: '#d1fae5', icon: '✅' },
  cancelled:      { label: 'Cancelled',        color: '#dc2626', bg: '#fee2e2', icon: '❌' },
};

const CANCELLABLE = ['Draft', 'Pending', 'draft', 'pending'];
const PAYABLE     = ['PaymentPending', 'payment_pending'];

const fmt = (date) => date
  ? new Date(date).toLocaleString('en-KE', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
  : '—';

const OrderDetails = ({ orderId }) => {
  const [order,    setOrder]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`orders/errands/${orderId}/`);
      setOrder(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load order.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // Poll while order is active (Pending / Assigned / InTransit)
  useEffect(() => {
    const active = ['Pending', 'Assigned', 'InTransit', 'pending', 'assigned', 'in_transit'];
    if (!order || !active.includes(order.status)) return;
    const t = setTimeout(fetchOrder, 15000);
    return () => clearTimeout(t);
  }, [order, fetchOrder]);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this order?')) return;
    setCancelling(true);
    try {
      await axios.post(`orders/${orderId}/cancel/`);
      fetchOrder();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not cancel order.');
    }
    setCancelling(false);
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ display:'inline-block', width:40, height:40, border:'4px solid #e5e7eb', borderTop:'4px solid #3b82f6', borderRadius:'50%', animation:'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{ background:'#fee2e2', border:'1px solid #f87171', borderRadius:8, padding:'16px 20px', color:'#b91c1c' }}>
      {error}
      <button onClick={() => navigate(-1)} style={{ display:'block', marginTop:12, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', fontSize:14 }}>← Go Back</button>
    </div>
  );

  if (!order) return null;

  const sc      = STATUS_CONFIG[order.status] || { label: order.status, color: '#374151', bg: '#f3f4f6', icon: '📋' };
  const rider   = order.assistant;
  const pricing = order.pricing_breakdown;

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: 640, margin: '0 auto', padding: '0 0 40px' }}>

      {/* Back */}
      <button onClick={() => navigate(-1)} style={{ background:'none', border:'none', color:'#3b82f6', cursor:'pointer', fontSize:14, marginBottom:16 }}>
        ← Back to Orders
      </button>

      {/* Header card */}
      <div style={{ background:'white', borderRadius:12, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
          <div>
            <h2 style={{ margin:0, fontSize:20, color:'#111827' }}>Order #{order.id}</h2>
            <p style={{ margin:'4px 0 0', fontSize:13, color:'#9ca3af' }}>Placed {fmt(order.created_at)}</p>
          </div>
          <div style={{ background:sc.bg, color:sc.color, borderRadius:20, padding:'6px 14px', fontSize:13, fontWeight:600 }}>
            {sc.icon} {sc.label}
          </div>
        </div>

        {/* Order type */}
        {order.order_type && (
          <div style={{ marginTop:16, padding:'10px 14px', background:'#f9fafb', borderRadius:8, fontSize:14, color:'#374151' }}>
            <span style={{ fontWeight:600 }}>{order.order_type.name}</span>
            {order.order_type.description && <span style={{ color:'#6b7280' }}> — {order.order_type.description}</span>}
          </div>
        )}
      </div>

      {/* Addresses */}
      {(order.pickup_address || order.delivery_address) && (
        <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 }}>
          <h3 style={sectionTitle}>Route</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {order.pickup_address && (
              <div style={addressCard('#eff6ff')}>
                <p style={addressLabel}>📍 Pickup</p>
                <p style={addressText}>{order.pickup_address}</p>
              </div>
            )}
            {order.delivery_address && (
              <div style={addressCard('#f0fdf4')}>
                <p style={addressLabel}>🏁 Delivery</p>
                <p style={addressText}>{order.delivery_address}</p>
              </div>
            )}
          </div>
          {order.distance_km && parseFloat(order.distance_km) > 0 && (
            <p style={{ margin:'10px 0 0', fontSize:13, color:'#6b7280' }}>
              Distance: {parseFloat(order.distance_km).toFixed(1)} km
            </p>
          )}
        </div>
      )}

      {/* Description */}
      {(order.description || order.item_description) && (
        <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 }}>
          <h3 style={sectionTitle}>Description</h3>
          <p style={{ fontSize:14, color:'#374151', whiteSpace:'pre-wrap', margin:0 }}>{order.description || order.item_description}</p>
        </div>
      )}

      {/* Receiver */}
      {(order.recipient_name || order.contact_number) && (
        <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 }}>
          <h3 style={sectionTitle}>Recipient</h3>
          {order.recipient_name && <p style={infoRow}><span style={infoLabel}>Name</span>{order.recipient_name}</p>}
          {order.contact_number  && <p style={infoRow}><span style={infoLabel}>Phone</span>{order.contact_number}</p>}
        </div>
      )}

      {/* Rider */}
      {rider ? (
        <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 }}>
          <h3 style={sectionTitle}>🏍️ Your Rider</h3>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:48, height:48, borderRadius:'50%', background:'#dbeafe', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
              {rider.first_name?.[0]?.toUpperCase() || '👤'}
            </div>
            <div>
              <p style={{ margin:0, fontWeight:600, color:'#111827', fontSize:15 }}>
                {rider.first_name || rider.name || ''} {rider.last_name || ''}
              </p>
              {(rider.phone_number || rider.phone) && (
                <a href={`tel:${rider.phone_number || rider.phone}`}
                   style={{ fontSize:13, color:'#3b82f6', textDecoration:'none' }}>
                  📞 {rider.phone_number || rider.phone}
                </a>
              )}
            </div>
          </div>
          {order.assigned_at && <p style={{ margin:'10px 0 0', fontSize:12, color:'#9ca3af' }}>Assigned {fmt(order.assigned_at)}</p>}
        </div>
      ) : ['Pending', 'pending'].includes(order.status) ? (
        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:12, padding:20, marginBottom:16, fontSize:14, color:'#92400e', textAlign:'center' }}>
          ⏳ Waiting for a rider to be assigned...
          <p style={{ margin:'6px 0 0', fontSize:12, color:'#b45309' }}>We'll notify you once a rider is on the way.</p>
        </div>
      ) : null}

      {/* Pricing */}
      <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 }}>
        <h3 style={sectionTitle}>Payment</h3>
        {(pricing || order.base_price) && (
          <div style={{ marginBottom:12 }}>
            {pricing?.base_fee    && <div style={priceRow}><span>Base Fee</span><span>KES {pricing.base_fee}</span></div>}
            {pricing?.distance_fee && <div style={priceRow}><span>Distance Fee</span><span>KES {pricing.distance_fee}</span></div>}
          </div>
        )}
        <div style={{ ...priceRow, fontWeight:700, fontSize:16, borderTop:'1px solid #e5e7eb', paddingTop:10 }}>
          <span>Total</span>
          <span>KES {parseFloat(order.price || order.total_price || 0).toFixed(2)}</span>
        </div>
        <div style={{ marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:13, color:'#6b7280' }}>Payment Status</span>
          <span style={{
            padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600,
            background: order.payment_status === 'paid' ? '#d1fae5' : '#fef3c7',
            color:       order.payment_status === 'paid' ? '#059669' : '#92400e',
          }}>
            {order.payment_status === 'paid' ? '✅ Paid' : '⏳ Unpaid'}
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 }}>
        <h3 style={sectionTitle}>Timeline</h3>
        {[
          { label: 'Order Placed',   value: order.created_at  },
          { label: 'Rider Assigned', value: order.assigned_at },
          { label: 'Picked Up',      value: order.started_at  },
          { label: 'Delivered',      value: order.completed_at },
          { label: 'Cancelled',      value: order.cancelled_at },
        ].filter(e => e.value).map(e => (
          <div key={e.label} style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:8, color:'#374151' }}>
            <span style={{ color:'#6b7280' }}>{e.label}</span>
            <span>{fmt(e.value)}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
        {PAYABLE.includes(order.status) && (
          <button
            onClick={() => navigate('/payment', { state: { order_id: order.id, amount: parseFloat(order.price || order.total_price || 0) } })}
            style={{ flex:1, padding:'13px 20px', background:'#10b981', color:'white', border:'none', borderRadius:50, fontSize:15, fontWeight:600, cursor:'pointer' }}
          >
            💳 Pay Now — KES {parseFloat(order.price || order.total_price || 0).toFixed(2)}
          </button>
        )}

        {CANCELLABLE.includes(order.status) && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            style={{ flex:'0 0 auto', padding:'13px 24px', background:'white', color:'#dc2626', border:'1px solid #fca5a5', borderRadius:50, fontSize:14, cursor:'pointer' }}
          >
            {cancelling ? 'Cancelling...' : '✕ Cancel Order'}
          </button>
        )}

        {order.status === 'Completed' && !order.review && (
          <button
            onClick={() => navigate(`/orders/${orderId}/review`)}
            style={{ flex:1, padding:'13px 20px', background:'#f59e0b', color:'white', border:'none', borderRadius:50, fontSize:15, fontWeight:600, cursor:'pointer' }}
          >
            ⭐ Leave a Review
          </button>
        )}
      </div>
    </div>
  );
};

// ── Inline styles ─────────────────────────────────────────────────────────────
const sectionTitle = { fontSize:14, fontWeight:700, color:'#374151', margin:'0 0 12px', textTransform:'uppercase', letterSpacing:'.4px' };
const addressCard  = (bg) => ({ background:bg, borderRadius:8, padding:'12px 14px' });
const addressLabel = { margin:'0 0 4px', fontSize:11, fontWeight:600, color:'#6b7280', textTransform:'uppercase' };
const addressText  = { margin:0, fontSize:14, color:'#111827' };
const infoRow      = { display:'flex', gap:8, fontSize:14, color:'#111827', margin:'0 0 8px' };
const infoLabel    = { color:'#6b7280', minWidth:80, flexShrink:0 };
const priceRow     = { display:'flex', justifyContent:'space-between', fontSize:14, color:'#374151', marginBottom:6 };

export default OrderDetails;
